/**
 * Helpers for the milestone goal condition (milestone-goal rule).
 *
 * The goal is only valid on outcomes of the milestone activity. The persisted
 * `ruleData` shape is:
 *   {
 *     goal: number,                    // integer from 1 to MILESTONE_GOAL_MAX
 *   }
 */
import { getInitialRuleData } from "../../../../../utils/commons";

export const MILESTONE_ACTIVITY_SLUG = "milestone";
export const MILESTONE_GOAL_RULE_SLUG = "milestone-goal";
// Upper bound of a signed 32-bit integer
export const MILESTONE_GOAL_MAX = 2147483647;
export const MILESTONE_GOAL_ERROR = `Enter a whole number between 1 and ${MILESTONE_GOAL_MAX}`;

export const isMilestoneActivity = (activity) =>
  activity?.activityDefinition?.slug === MILESTONE_ACTIVITY_SLUG;

export const isMilestoneGoalRule = (rule) =>
  rule?.ruleDefinition?.slug === MILESTONE_GOAL_RULE_SLUG;

export const getMilestoneGoalRuleDefinition = (outcome) =>
  (outcome?.outcomeDefinition?.rules || outcome?.rules || []).find(
    (rule) => rule?.slug === MILESTONE_GOAL_RULE_SLUG
  );

export const requiresMilestoneGoal = (activity, outcome) =>
  isMilestoneActivity(activity) && !!getMilestoneGoalRuleDefinition(outcome);

export const isValidMilestoneGoal = (goal) =>
  Number.isSafeInteger(goal) && goal >= 1 && goal <= MILESTONE_GOAL_MAX;

export const getMilestoneGoal = (outcome) =>
  outcome?.outcomeRules?.find(isMilestoneGoalRule)?.ruleData?.goal;

export const hasValidMilestoneGoal = (outcome) =>
  isValidMilestoneGoal(getMilestoneGoal(outcome));

/**
 * Appends the goal rule when the outcome needs it and does not carry it yet.
 * It is never prepended: AwardPointsComponent reads outcomeRules[0].
 * When previousOutcome carries a goal rule it is reused, so the goal survives
 * modals that drop rules they do not render.
 */
export const ensureMilestoneGoalRule = (activity, outcome, previousOutcome) => {
  if (!requiresMilestoneGoal(activity, outcome)) return outcome;
  const rules = outcome?.outcomeRules || [];
  if (rules.some(isMilestoneGoalRule)) return outcome;
  const definition = getMilestoneGoalRuleDefinition(outcome);
  const previousRule = previousOutcome?.outcomeRules?.find(isMilestoneGoalRule);
  return {
    ...outcome,
    outcomeRules: [
      ...rules,
      previousRule || {
        ruleDefinition: definition,
        ruleData: { ...getInitialRuleData(definition) },
      },
    ],
  };
};

export const removeMilestoneGoalRule = (outcome) =>
  outcome?.outcomeRules?.some(isMilestoneGoalRule)
    ? {
        ...outcome,
        outcomeRules: outcome.outcomeRules.filter(
          (rule) => !isMilestoneGoalRule(rule)
        ),
      }
    : outcome;

export const setMilestoneGoal = (activity, outcome, goal) => {
  const withRule = ensureMilestoneGoalRule(activity, outcome);
  return {
    ...withRule,
    outcomeRules: (withRule?.outcomeRules || []).map((rule) =>
      isMilestoneGoalRule(rule)
        ? { ...rule, ruleData: goal === undefined ? {} : { goal } }
        : rule
    ),
  };
};

// Milestone activity: add the rule where it is needed.
// Any other activity: strip it, it must never be attached there.
export const normalizeMilestoneGoalRules = (activity) => {
  if (!activity?.outcomes) return activity;
  const outcomes = activity.outcomes.map((outcome) =>
    isMilestoneActivity(activity)
      ? ensureMilestoneGoalRule(activity, outcome)
      : removeMilestoneGoalRule(outcome)
  );
  return { ...activity, outcomes };
};

export const getMissingMilestoneGoalMessage = (outcome) =>
  `Please enter a valid milestone goal for ${
    outcome?.outcomeDefinition?.name ?? "each outcome"
  }`;

export const findOutcomeMissingMilestoneGoal = (activity) =>
  isMilestoneActivity(activity)
    ? (activity.outcomes || []).find(
        (outcome) =>
          requiresMilestoneGoal(activity, outcome) && !hasValidMilestoneGoal(outcome)
      )
    : undefined;
